import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Bell, Eye, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/uiDir/button";
import { Input } from "@/components/uiDir/input";
import { Label } from "@/components/uiDir/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/uiDir/card";
import { Switch } from "@/components/uiDir/switch";
import { Separator } from "@/components/uiDir/separator";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/uiDir/alert-dialog";

export default function Settings() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [settings, setSettings] = useState({
    emailNotifications: true,
    profileVisibility: true,
    showEmail: false,
    showPhone: false,
    allowMessages: true,
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    loadUserSettings();
  }, []);

  const loadUserSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/login');
        return;
      }

      setUserId(user.id);

      // Load profile settings
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('email_public, phone_public')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      if (profile) {
        setSettings(prev => ({
          ...prev,
          showEmail: profile.email_public || false,
          showPhone: profile.phone_public || false,
        }));
      }

      setIsLoadingSettings(false);
    } catch (err) {
      console.error('Error loading settings:', err);
      toast.error('Failed to load settings');
      setIsLoadingSettings(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("Passwords don't match!");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });

      if (error) throw error;

      toast.success("Password updated successfully!");
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (err) {
      console.error('Error updating password:', err);
      toast.error(err.message || 'Failed to update password');
    } finally {
      setIsLoading(false);
    }
  };

  const updatePrivacySetting = async (field: 'email_public' | 'phone_public', value: boolean) => {
    if (!userId) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ [field]: value })
        .eq('id', userId);

      if (error) throw error;

      toast.success(`${field === 'email_public' ? 'Email' : 'Phone'} visibility updated`);
    } catch (err) {
      console.error('Error updating privacy setting:', err);
      toast.error('Failed to update setting');
    }
  };

  const handleDeleteAccount = async () => {
    if (!userId) return;

    try {
      // Delete user profile (cascade will delete related data)
      const { error: deleteError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);

      if (deleteError) throw deleteError;

      // Sign out
      await supabase.auth.signOut();

      toast.success("Account deleted successfully");
      navigate('/');
    } catch (err) {
      console.error('Error deleting account:', err);
      toast.error(err.message || 'Failed to delete account');
    }
  };

  if (isLoadingSettings) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 max-w-4xl">
        <div className="mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </Link>
          <h1 className="text-4xl font-heading font-bold mb-2">Settings</h1>
          <p className="text-muted-foreground">Manage your account preferences</p>
        </div>

        <div className="space-y-6">
          {/* Password Change */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Change Password
              </CardTitle>
              <CardDescription>Update your password regularly for security</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                    minLength={8}
                  />
                  <p className="text-xs text-muted-foreground">
                    Must be at least 8 characters
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                  />
                </div>

                <Button type="submit" disabled={isLoading} className="bg-gradient-primary">
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Password"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notifications
              </CardTitle>
              <CardDescription>Manage how you receive updates</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Email Notifications</Label>
                  <p className="text-sm text-muted-foreground">Receive updates via email</p>
                </div>
                <Switch
                  checked={settings.emailNotifications}
                  onCheckedChange={(checked) => {
                    setSettings({ ...settings, emailNotifications: checked });
                    toast.success(checked ? "Email notifications enabled" : "Email notifications disabled");
                  }}
                />
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>New Followers</Label>
                  <p className="text-sm text-muted-foreground">Get notified when someone follows you</p>
                </div>
                <Switch defaultChecked />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Project Likes</Label>
                  <p className="text-sm text-muted-foreground">Get notified when someone likes your project</p>
                </div>
                <Switch defaultChecked />
              </div>
            </CardContent>
          </Card>

          {/* Privacy */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Privacy Settings
              </CardTitle>
              <CardDescription>Control what others can see</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Show Email</Label>
                  <p className="text-sm text-muted-foreground">Display email on public profile</p>
                </div>
                <Switch
                  checked={settings.showEmail}
                  onCheckedChange={(checked) => {
                    setSettings({ ...settings, showEmail: checked });
                    updatePrivacySetting('email_public', checked);
                  }}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Show Phone</Label>
                  <p className="text-sm text-muted-foreground">Display phone number on public profile</p>
                </div>
                <Switch
                  checked={settings.showPhone}
                  onCheckedChange={(checked) => {
                    setSettings({ ...settings, showPhone: checked });
                    updatePrivacySetting('phone_public', checked);
                  }}
                />
              </div>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="glass-card border-destructive/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Danger Zone
              </CardTitle>
              <CardDescription>Irreversible actions</CardDescription>
            </CardHeader>
            <CardContent>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="w-full">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Account
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete your account and remove all your data from our servers.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteAccount} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Delete Account
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}


// import { useState } from "react";
// import { Link } from "react-router-dom";
// import { ArrowLeft, Lock, Bell, Eye, Trash2, Loader2 } from "lucide-react";
// import { Button } from "@/components/ui/button";
// import { Input } from "@/components/ui/input";
// import { Label } from "@/components/ui/label";
// import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
// import { Switch } from "@/components/ui/switch";
// import { Separator } from "@/components/ui/separator";
// import { toast } from "sonner";
// import {
//   AlertDialog,
//   AlertDialogAction,
//   AlertDialogCancel,
//   AlertDialogContent,
//   AlertDialogDescription,
//   AlertDialogFooter,
//   AlertDialogHeader,
//   AlertDialogTitle,
//   AlertDialogTrigger,
// } from "@/components/ui/alert-dialog";

// export default function Settings() {
//   const [isLoading, setIsLoading] = useState(false);
//   const [settings, setSettings] = useState({
//     emailNotifications: true,
//     profileVisibility: true,
//     showEmail: false,
//     showPhone: false,
//     allowMessages: true,
//   });

//   const [passwordData, setPasswordData] = useState({
//     currentPassword: "",
//     newPassword: "",
//     confirmPassword: "",
//   });

//   const handlePasswordChange = async (e: React.FormEvent) => {
//     e.preventDefault();
    
//     if (passwordData.newPassword !== passwordData.confirmPassword) {
//       toast.error("Passwords don't match!");
//       return;
//     }

//     setIsLoading(true);
//     await new Promise(resolve => setTimeout(resolve, 1500));
    
//     toast.success("Password updated successfully!");
//     setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
//     setIsLoading(false);
//   };

//   const handleDeleteAccount = async () => {
//     toast.error("Account deletion is not implemented yet");
//   };

//   return (
//     <div className="min-h-screen bg-gradient-subtle">
//       <div className="container mx-auto px-4 py-8 max-w-4xl">
//         <div className="mb-8">
//           <Link to="/dashboard">
//             <Button variant="ghost" className="mb-4">
//               <ArrowLeft className="mr-2 h-4 w-4" />
//               Back to Dashboard
//             </Button>
//           </Link>
//           <h1 className="text-4xl font-heading font-bold mb-2">Settings</h1>
//           <p className="text-muted-foreground">Manage your account preferences</p>
//         </div>

//         <div className="space-y-6">
//           {/* Password Change */}
//           <Card className="glass-card">
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2">
//                 <Lock className="h-5 w-5" />
//                 Change Password
//               </CardTitle>
//               <CardDescription>Update your password regularly for security</CardDescription>
//             </CardHeader>
//             <CardContent>
//               <form onSubmit={handlePasswordChange} className="space-y-4">
//                 <div className="space-y-2">
//                   <Label htmlFor="currentPassword">Current Password</Label>
//                   <Input
//                     id="currentPassword"
//                     type="password"
//                     value={passwordData.currentPassword}
//                     onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
//                     required
//                   />
//                 </div>

//                 <div className="space-y-2">
//                   <Label htmlFor="newPassword">New Password</Label>
//                   <Input
//                     id="newPassword"
//                     type="password"
//                     value={passwordData.newPassword}
//                     onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
//                     required
//                     minLength={8}
//                   />
//                   <p className="text-xs text-muted-foreground">
//                     Must be at least 8 characters with uppercase, lowercase, number, and special character
//                   </p>
//                 </div>

//                 <div className="space-y-2">
//                   <Label htmlFor="confirmPassword">Confirm New Password</Label>
//                   <Input
//                     id="confirmPassword"
//                     type="password"
//                     value={passwordData.confirmPassword}
//                     onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
//                     required
//                   />
//                 </div>

//                 <Button type="submit" disabled={isLoading} className="bg-gradient-primary">
//                   {isLoading ? (
//                     <>
//                       <Loader2 className="mr-2 h-4 w-4 animate-spin" />
//                       Updating...
//                     </>
//                   ) : (
//                     "Update Password"
//                   )}
//                 </Button>
//               </form>
//             </CardContent>
//           </Card>

//           {/* Notifications */}
//           <Card className="glass-card">
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2">
//                 <Bell className="h-5 w-5" />
//                 Notifications
//               </CardTitle>
//               <CardDescription>Manage how you receive updates</CardDescription>
//             </CardHeader>
//             <CardContent className="space-y-4">
//               <div className="flex items-center justify-between">
//                 <div className="space-y-0.5">
//                   <Label>Email Notifications</Label>
//                   <p className="text-sm text-muted-foreground">Receive updates via email</p>
//                 </div>
//                 <Switch
//                   checked={settings.emailNotifications}
//                   onCheckedChange={(checked) => {
//                     setSettings({ ...settings, emailNotifications: checked });
//                     toast.success(checked ? "Email notifications enabled" : "Email notifications disabled");
//                   }}
//                 />
//               </div>

//               <Separator />

//               <div className="flex items-center justify-between">
//                 <div className="space-y-0.5">
//                   <Label>New Followers</Label>
//                   <p className="text-sm text-muted-foreground">Get notified when someone follows you</p>
//                 </div>
//                 <Switch defaultChecked />
//               </div>

//               <div className="flex items-center justify-between">
//                 <div className="space-y-0.5">
//                   <Label>Project Likes</Label>
//                   <p className="text-sm text-muted-foreground">Get notified when someone likes your project</p>
//                 </div>
//                 <Switch defaultChecked />
//               </div>
//             </CardContent>
//           </Card>

//           {/* Privacy */}
//           <Card className="glass-card">
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2">
//                 <Eye className="h-5 w-5" />
//                 Privacy Settings
//               </CardTitle>
//               <CardDescription>Control what others can see</CardDescription>
//             </CardHeader>
//             <CardContent className="space-y-4">
//               <div className="flex items-center justify-between">
//                 <div className="space-y-0.5">
//                   <Label>Profile Visibility</Label>
//                   <p className="text-sm text-muted-foreground">Make your profile public</p>
//                 </div>
//                 <Switch
//                   checked={settings.profileVisibility}
//                   onCheckedChange={(checked) => {
//                     setSettings({ ...settings, profileVisibility: checked });
//                     toast.success(checked ? "Profile is now public" : "Profile is now private");
//                   }}
//                 />
//               </div>

//               <Separator />

//               <div className="flex items-center justify-between">
//                 <div className="space-y-0.5">
//                   <Label>Show Email</Label>
//                   <p className="text-sm text-muted-foreground">Display email on public profile</p>
//                 </div>
//                 <Switch
//                   checked={settings.showEmail}
//                   onCheckedChange={(checked) => setSettings({ ...settings, showEmail: checked })}
//                 />
//               </div>

//               <div className="flex items-center justify-between">
//                 <div className="space-y-0.5">
//                   <Label>Show Phone</Label>
//                   <p className="text-sm text-muted-foreground">Display phone number on public profile</p>
//                 </div>
//                 <Switch
//                   checked={settings.showPhone}
//                   onCheckedChange={(checked) => setSettings({ ...settings, showPhone: checked })}
//                 />
//               </div>

//               <div className="flex items-center justify-between">
//                 <div className="space-y-0.5">
//                   <Label>Allow Messages</Label>
//                   <p className="text-sm text-muted-foreground">Let others contact you</p>
//                 </div>
//                 <Switch
//                   checked={settings.allowMessages}
//                   onCheckedChange={(checked) => setSettings({ ...settings, allowMessages: checked })}
//                 />
//               </div>
//             </CardContent>
//           </Card>

//           {/* Danger Zone */}
//           <Card className="glass-card border-destructive/50">
//             <CardHeader>
//               <CardTitle className="flex items-center gap-2 text-destructive">
//                 <Trash2 className="h-5 w-5" />
//                 Danger Zone
//               </CardTitle>
//               <CardDescription>Irreversible actions</CardDescription>
//             </CardHeader>
//             <CardContent>
//               <AlertDialog>
//                 <AlertDialogTrigger asChild>
//                   <Button variant="destructive" className="w-full">
//                     <Trash2 className="mr-2 h-4 w-4" />
//                     Delete Account
//                   </Button>
//                 </AlertDialogTrigger>
//                 <AlertDialogContent>
//                   <AlertDialogHeader>
//                     <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
//                     <AlertDialogDescription>
//                       This action cannot be undone. This will permanently delete your account and remove all your data from our servers.
//                     </AlertDialogDescription>
//                   </AlertDialogHeader>
//                   <AlertDialogFooter>
//                     <AlertDialogCancel>Cancel</AlertDialogCancel>
//                     <AlertDialogAction onClick={handleDeleteAccount} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
//                       Delete Account
//                     </AlertDialogAction>
//                   </AlertDialogFooter>
//                 </AlertDialogContent>
//               </AlertDialog>
//             </CardContent>
//           </Card>
//         </div>
//       </div>
//     </div>
//   );
// }
